import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '../../services/apiService';
import { toast } from 'react-toastify';

export function PasswordForm() {
  const [formData, setFormData] = useState({ username: '' });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validatePassword = () => {
    const newErrors = {};
    if (!formData.username) {
      newErrors.username = 'Email or mobile is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePassword = async (event) => {
    event.preventDefault();
    if (!validatePassword()) return;
    try {
      await requestPasswordReset(formData.username);
      toast.success('Password reset link sent successfully!', {
        autoClose: 2000,
      });
      navigate('/login');
    } catch (error) {
      toast.error('Failed to send password reset email. Please try again.');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box
      component="form"
      onSubmit={handlePassword}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        px: { xs: 1, sm: 2 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Typography
        variant="h4"
        fontWeight={600}
        sx={{
          color: '#1a1a1a',
          textAlign: 'center',
          mb: 1.5,
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 500,
        }}
      >
        Forget Password?
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          color: '#666666',
          fontWeight: 400,
          textAlign: 'center',
          mb: 3,
          fontSize: { xs: '0.95rem', sm: '0.8rem' },
        }}
      >
        Enter your phone number or email and we will send you a link to reset
        your password.
      </Typography>
      <TextField
        fullWidth
        name="username"
        placeholder="Enter your email or mobile number"
        value={formData.username}
        onChange={handleChange}
        error={!!errors.username}
        helperText={errors.username}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email
                sx={{
                  color: '#9ca3af',
                  fontSize: { xs: '1.2rem', sm: '1.4rem' },
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2.5,
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
            '& fieldset': {
              borderColor: '#e5e7eb',
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: '#d1d5db',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3b82f6',
              borderWidth: '2px',
            },
          },
          '& .MuiInputBase-input': {
            height: '36px',
            padding: '8px 14px',
            color: '#1f2937',
            '&::placeholder': {
              color: '#9ca3af',
              opacity: 1,
            },
          },
        }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{
          height: '48px',
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '1rem',
          mb: 3,
          background: '#4F46E5',
          color: '#ffffff',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
          '&:hover': {
            background: '#4338CA',
            boxShadow: '0 6px 16px rgba(79, 70, 229, 0.4)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        Sent Reset Link
      </Button>
      <Link
        sx={{
          fontSize: '1rem',
          textDecoration: 'none',
          textAlign: 'center',
          color: '#6b7280',
          cursor: 'pointer',
          mt: 1,
          '&:hover': {
            textDecoration: 'underline',
            color: '#374151',
          },
        }}
        onClick={() => navigate('/login')}
      >
        Back
      </Link>
    </Box>
  );
}

export default PasswordForm;
